/**
 * API client.
 *
 * Every failure is turned into an `ApiError` carrying the RFC 7807 `code`, so
 * screens branch on a stable identifier instead of the English `detail`. The
 * detail is kept as a last-resort message, which matters because a non-2xx
 * response is not always a problem document: a proxy returns HTML for a 502 and
 * that has no `code` to switch on.
 */

import {
  asProblemDetails,
  fieldErrorsFromIssues,
  type Branch,
  type ChangePasswordInput,
  type ErrorCode,
  type LoginInput,
  type Page,
  type ProblemDetails,
  type SessionUser,
} from '@retailpro/shared';

const API_PREFIX = '/api/v1';

/** Access tokens are short-lived; refreshing on 401 beats logging the user out. */
const TOKEN_TTL_BUFFER_MS = 15_000;

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'NETWORK_ERROR' | 'UNEXPECTED_RESPONSE';
  readonly problem: ProblemDetails | null;
  readonly correlationId: string | undefined;

  constructor(
    code: ApiError['code'],
    status: number,
    message: string,
    problem: ProblemDetails | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.problem = problem;
    this.correlationId = problem?.correlationId;
  }

  /** Field-level messages for a validation failure, if this is one. */
  get fieldErrors(): Record<string, string[]> {
    return this.problem?.errors ?? {};
  }

  get isUnauthenticated(): boolean {
    return (
      this.status === 401 || this.code === 'UNAUTHENTICATED' || this.code === 'INVALID_CREDENTIALS'
    );
  }
}

interface TokenBundle {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface Session {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: SessionUser;
}

/**
 * Tokens live in `sessionStorage`, not `localStorage`: an XSS payload that runs
 * in this origin can still read either, but `sessionStorage` is discarded when
 * the tab closes, which limits the window on a shared terminal. The refresh token
 * is the higher-value secret and should move to an `HttpOnly` cookie once the
 * deployment sits behind HTTPS — tracked in the deployment notes.
 */
const STORAGE_KEY = 'retailpro.session';

export function loadSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as Session;
  } catch {
    // A corrupt entry must not wedge the app on a white screen.
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function saveSession(session: Session): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function apiBaseUrl(): string {
  // Same-origin by default. A split deployment (CDN front end, API on its own
  // host) sets VITE_API_BASE_URL and relies on the API's CORS allowlist.
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

function tokensFrom(session: Session): TokenBundle {
  return {
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    expiresAt: Date.now() + session.expiresIn * 1000,
  };
}

export function isAccessTokenFresh(session: Session): boolean {
  return Date.now() < tokensFrom(session).expiresAt - TOKEN_TTL_BUFFER_MS;
}

/**
 * Build the same error shape a `422` from the API would produce, for a form the
 * client rejected before sending it.
 *
 * Rendering local and remote validation failures through one code path means a
 * field message does not appear while typing and then vanish, or vice versa.
 */
export function clientValidationError(
  issues: { path: (string | number | symbol)[]; message: string }[],
  detail = 'The submitted data is invalid.',
): ApiError {
  const problem: ProblemDetails = {
    type: 'https://retailpro.dev/problems/validation-failed',
    title: 'Validation failed',
    status: 422,
    detail,
    code: 'VALIDATION_FAILED',
    errors: fieldErrorsFromIssues(issues, 'form'),
    timestamp: new Date().toISOString(),
  };
  return new ApiError('VALIDATION_FAILED', 422, detail, problem);
}

export class ApiClient {
  constructor(
    private readonly baseUrl: string = apiBaseUrl(),
    private readonly onSessionLost?: () => void,
  ) {}

  async login(input: LoginInput): Promise<Session> {
    const session = await this.request<Session>('POST', '/auth/login', { body: input });
    saveSession(session);
    return session;
  }

  async logout(session: Session): Promise<void> {
    try {
      await this.request('POST', '/auth/logout', {
        body: { refreshToken: session.refreshToken },
        accessToken: session.accessToken,
      });
    } catch {
      // Local sign-out must succeed even if the server cannot be told; the
      // refresh token expires on its own and the user asked to leave.
    } finally {
      clearSession();
    }
  }

  /**
   * Change the signed-in user's password.
   *
   * Every refresh token is revoked on success, including this session's, so there
   * is deliberately no return value carrying a usable session. On success the
   * local session is dropped and the session-lost callback fires, which is what
   * sends the user back to the login screen; the old token is then provably dead
   * rather than merely expected to expire.
   *
   * On failure nothing is cleared. A 401 here means "that is not the current
   * password", which is a typo the user can fix, not an expired token — so this
   * call opts out of both the automatic refresh and the session-lost handler.
   * Ending the session on a mistyped password would throw the operator out of the
   * one screen that lets them recover.
   */
  async changePassword(input: ChangePasswordInput, accessToken: string): Promise<void> {
    await this.request('POST', '/auth/change-password', {
      body: input,
      accessToken,
      skipRefresh: true,
      keepSessionOnAuthError: true,
    });
    clearSession();
    this.onSessionLost?.();
  }

  /**
   * Revoke every session for this account.
   *
   * Distinct from `logout`, which revokes only the token it is handed. This is
   * the button to reach for when a till may have been compromised and cannot be
   * physically reached.
   *
   * Like `logout`, this ends the local session even if the server cannot be told:
   * the user asked to be signed out everywhere, and refusing to close the screen
   * would leave the compromised tab looking signed in.
   */
  async logoutAll(accessToken: string): Promise<void> {
    try {
      await this.request('POST', '/auth/logout-all', { accessToken });
    } finally {
      clearSession();
      this.onSessionLost?.();
    }
  }

  async health(): Promise<unknown> {
    return this.request('GET', '/health');
  }

  async version(): Promise<unknown> {
    return this.request('GET', '/version');
  }

  async branches(accessToken: string): Promise<Page<Branch>> {
    return this.request('GET', '/branches?limit=50', { accessToken });
  }

  /**
   * Refresh once per token generation and let queued callers share the result.
   * Without the in-flight promise, a dashboard firing four parallel requests at
   * an expired token would send four refreshes and invalidate its own new
   * refresh token, logging the user straight back out.
   */
  private refreshing: Promise<Session> | null = null;

  private async refresh(): Promise<Session> {
    this.refreshing ??= (async () => {
      const current = loadSession();
      if (!current) throw new ApiError('UNAUTHENTICATED', 401, 'No active session.');

      const refreshed = await this.request<Session>('POST', '/auth/refresh', {
        body: { refreshToken: current.refreshToken },
        skipRefresh: true,
      });
      saveSession(refreshed);
      return refreshed;
    })();

    try {
      return await this.refreshing;
    } finally {
      this.refreshing = null;
    }
  }

  private async request<T>(
    method: string,
    path: string,
    options: {
      body?: unknown;
      accessToken?: string;
      skipRefresh?: boolean;
      /** Set where a 401 means "bad credentials", not "expired session". */
      keepSessionOnAuthError?: boolean;
    } = {},
  ): Promise<T> {
    const session = loadSession();
    const accessToken = options.accessToken ?? session?.accessToken;

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${API_PREFIX}${path}`, {
        method,
        headers: {
          Accept: 'application/json',
          ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        credentials: 'include',
      });
    } catch {
      // A rejected fetch means DNS, TLS, CORS or an offline terminal. All four
      // are the same thing to the user: the till cannot reach the server.
      throw new ApiError('NETWORK_ERROR', 0, 'The server could not be reached.', null);
    }

    if (response.status === 401 && !options.skipRefresh && session?.refreshToken) {
      const renewed = await this.refresh();
      return this.request<T>(method, path, { ...options, accessToken: renewed.accessToken });
    }

    if (!response.ok) {
      // `options` is threaded through on the refresh retry above, so a caller
      // that opted out of session loss stays opted out if a retry happens.
      throw await this.toError(response, options.keepSessionOnAuthError === true);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  private async toError(response: Response, keepSessionOnAuthError = false): Promise<ApiError> {
    let body: unknown = null;
    try {
      const text = await response.text();
      body = text ? JSON.parse(text) : null;
    } catch {
      // Not JSON. Almost certainly a proxy or gateway page, and the status is
      // still meaningful.
    }

    const problem = asProblemDetails(body);
    if (!problem) {
      return new ApiError(
        'UNEXPECTED_RESPONSE',
        response.status,
        `Request failed with status ${response.status}.`,
      );
    }

    const error = new ApiError(problem.code, response.status, problem.detail, problem);
    if (error.isUnauthenticated && !keepSessionOnAuthError) this.onSessionLost?.();
    return error;
  }
}
